import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowLeft, ArrowRight } from "lucide-react";
import { API, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Field, ErrBox } from "../../components/common";

const STEPS = ["Account", "Business", "Tax", "Accounting", "Review"];
const ENTITY_TYPES = ["LLC", "S-Corp", "C-Corp", "Sole Proprietor", "Partnership", "Non-Profit"];
const FREQUENCIES = ["", "monthly", "quarterly", "annual", "unsure"];

export default function Register() {
  const [params] = useSearchParams();
  const { reload } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [f, setF] = useState({
    name: "", email: "", phone: "", password: "", confirm: "",
    legal_name: "", dba: "", entity_type: "LLC", ein: "", state: "AZ",
    addr1: "", city: "", zip: "", mail_same: true, website: "", industry: "", start_date: "",
    tpt_license: "", filing_frequency: "", state_withholding_account: "", payroll_tax_info: "", other_registrations: "",
    basis: "cash", fiscal_year: "calendar",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const validStep = () => {
    if (step === 0) return f.name && f.email && f.password.length >= 8 && f.password === f.confirm;
    if (step === 1) return f.legal_name && f.city && f.zip && f.ein.replace(/\D/g, "").length === 9;
    return true;
  };

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await API.post("/auth/register", {
        email: f.email, password: f.password, name: f.name, phone: f.phone,
        business: {
          legal_name: f.legal_name, dba: f.dba, entity_type: f.entity_type, ein: f.ein, state: f.state,
          address: { line1: f.addr1, city: f.city, state: f.state, zip: f.zip },
          mailing_address: f.mail_same ? { line1: f.addr1, city: f.city, state: f.state, zip: f.zip } : {},
          phone: f.phone, website: f.website, industry: f.industry, start_date: f.start_date,
        },
        tax: { tpt_license: f.tpt_license, filing_frequency: f.filing_frequency,
               state_withholding_account: f.state_withholding_account,
               payroll_tax_info: f.payroll_tax_info, other_registrations: f.other_registrations },
        accounting: { basis: f.basis, fiscal_year: f.fiscal_year },
      });
      await reload();
      const plan = params.get("plan");
      navigate(plan ? `/portal/settings?tab=billing&plan=${plan}&interval=${params.get("interval") || "monthly"}` : "/portal");
    } catch (e) { setErr(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="" className="h-10 w-10 rounded-full" />
            <span className="font-extrabold text-navy">AZ <span className="text-copper">TAX</span> ACCOUNTS</span>
          </Link>
          <Link to="/login" className="text-sm font-semibold text-navy hover:underline">Sign in</Link>
        </div>

        <div className="mb-6 flex items-center gap-1" data-testid="register-steps">
          {STEPS.map((s, i) => (
            <div key={s} className="flex flex-none items-center gap-1">
              <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${i < step ? "bg-emerald-500 text-white" : i === step ? "bg-navy text-white" : "bg-slate-200 text-slate-500"}`}>
                {i < step ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`whitespace-nowrap text-xs font-semibold ${i === step ? "text-navy" : "text-slate-400"}`}>{s}</span>
              {i < STEPS.length - 1 && <div className="mx-1 h-px w-4 bg-slate-300 sm:w-8" />}
            </div>
          ))}
        </div>

        <div className="card-gov p-6" data-testid="register-form">
          {step === 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-navy">Step 1: Account Information</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Full Name *"><input className="input-gov" value={f.name} onChange={set("name")} data-testid="reg-name" /></Field>
                <Field label="Phone"><input className="input-gov" value={f.phone} onChange={set("phone")} data-testid="reg-phone" /></Field>
                <Field label="Email *"><input type="email" className="input-gov" value={f.email} onChange={set("email")} data-testid="reg-email" /></Field>
                <div />
                <Field label="Password *" hint="Minimum 8 characters"><input type="password" className="input-gov" value={f.password} onChange={set("password")} data-testid="reg-password" /></Field>
                <Field label="Confirm Password *"><input type="password" className="input-gov" value={f.confirm} onChange={set("confirm")} data-testid="reg-confirm" /></Field>
              </div>
              {f.password && f.confirm && f.password !== f.confirm && <div className="text-sm text-rose-700">Passwords do not match.</div>}
            </div>
          )}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-navy">Step 2: Business Information</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Legal Business Name *"><input className="input-gov" value={f.legal_name} onChange={set("legal_name")} data-testid="reg-legal-name" /></Field>
                <Field label="DBA"><input className="input-gov" value={f.dba} onChange={set("dba")} data-testid="reg-dba" /></Field>
                <Field label="Entity Type">
                  <select className="input-gov" value={f.entity_type} onChange={set("entity_type")} data-testid="reg-entity-type">
                    {ENTITY_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="EIN *" hint="Required. 9 digits, e.g. 86-1234567. Stored securely, masked in the UI"><input className="input-gov font-mono" placeholder="12-3456789" value={f.ein} onChange={set("ein")} required data-testid="reg-ein" /></Field>
                <Field label="State"><input className="input-gov" value={f.state} onChange={set("state")} data-testid="reg-state" /></Field>
                <Field label="Business Start Date"><input type="date" className="input-gov" value={f.start_date} onChange={set("start_date")} data-testid="reg-start-date" /></Field>
                <Field label="Street Address"><input className="input-gov" value={f.addr1} onChange={set("addr1")} data-testid="reg-address" /></Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="City *"><input className="input-gov" value={f.city} onChange={set("city")} data-testid="reg-city" /></Field>
                  <Field label="ZIP *"><input className="input-gov" value={f.zip} onChange={set("zip")} data-testid="reg-zip" /></Field>
                </div>
                <Field label="Website"><input className="input-gov" value={f.website} onChange={set("website")} data-testid="reg-website" /></Field>
                <Field label="Industry"><input className="input-gov" value={f.industry} onChange={set("industry")} data-testid="reg-industry" /></Field>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={f.mail_same} onChange={(e) => setF({ ...f, mail_same: e.target.checked })} /> Mailing address is the same as business address
              </label>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-navy">Step 3: Tax Information</h2>
              <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
                Filing frequency is never guessed. If you are unsure, select "unsure" and an administrator will verify and configure the correct filing rules for your account.
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Arizona TPT License Number"><input className="input-gov" value={f.tpt_license} onChange={set("tpt_license")} data-testid="reg-tpt" /></Field>
                <Field label="Filing Frequency">
                  <select className="input-gov" value={f.filing_frequency} onChange={set("filing_frequency")} data-testid="reg-filing-frequency">
                    <option value="">Select…</option>
                    {FREQUENCIES.filter(Boolean).map((q) => <option key={q} value={q}>{q === "unsure" ? "Not sure — administrator review needed" : q[0].toUpperCase() + q.slice(1)}</option>)}
                  </select>
                </Field>
                <Field label="State Withholding Account"><input className="input-gov" value={f.state_withholding_account} onChange={set("state_withholding_account")} data-testid="reg-withholding" /></Field>
                <Field label="Payroll Tax Information"><input className="input-gov" value={f.payroll_tax_info} onChange={set("payroll_tax_info")} data-testid="reg-payroll" /></Field>
              </div>
              <Field label="Other Tax Registrations"><textarea className="input-gov" rows={2} value={f.other_registrations} onChange={set("other_registrations")} data-testid="reg-other-tax" /></Field>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-navy">Step 4: Accounting Settings</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Accounting Basis">
                  <select className="input-gov" value={f.basis} onChange={set("basis")} data-testid="reg-basis">
                    <option value="cash">Cash basis</option><option value="accrual">Accrual basis</option>
                  </select>
                </Field>
                <Field label="Tax Year">
                  <select className="input-gov" value={f.fiscal_year} onChange={set("fiscal_year")} data-testid="reg-fiscal-year">
                    <option value="calendar">Calendar year</option><option value="fiscal">Fiscal year</option>
                  </select>
                </Field>
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                <div className="font-semibold text-navy">Step 5 (after signup): Connect Financial Services</div>
                Once your account is created you can connect Stripe, Square, Clover, Shopify, PayPal, QuickBooks, Toast, bank feeds, and ACH providers from your portal. Connection status is always shown honestly: Connected, Not Connected, or Action Required.
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-navy">Step 5: Review & Create Account</h2>
              <dl className="grid gap-2 text-sm sm:grid-cols-2">
                {[["Name", f.name], ["Email", f.email], ["Business", f.legal_name], ["Entity", f.entity_type],
                  ["City/State", `${f.city}, ${f.state}`], ["TPT License", f.tpt_license || "—"],
                  ["Filing Frequency", f.filing_frequency || "To be verified by administrator"],
                  ["Accounting", `${f.basis} basis / ${f.fiscal_year} year`]].map(([k, v]) => (
                  <div key={k} className="rounded border border-slate-200 px-3 py-2"><dt className="text-xs uppercase tracking-widest text-slate-400">{k}</dt><dd className="font-semibold text-slate-700">{v}</dd></div>
                ))}
              </dl>
              <p className="text-xs text-slate-500">By creating an account you agree to the <Link to="/legal/terms" className="text-navy underline">Terms of Service</Link>, <Link to="/legal/privacy" className="text-navy underline">Privacy Policy</Link>, and <Link to="/legal/e-communications" className="text-navy underline">Electronic Communications Consent</Link>.</p>
            </div>
          )}

          <ErrBox msg={err} />
          <div className="mt-6 flex justify-between">
            <button onClick={() => setStep(step - 1)} disabled={step === 0 || busy} className="btn-outline-gov disabled:opacity-40" data-testid="reg-back">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            {step < 4 ? (
              <button onClick={() => validStep() && setStep(step + 1)} disabled={!validStep()} className="btn-navy" data-testid="reg-next">
                Next <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={submit} disabled={busy} className="btn-copper px-6" data-testid="reg-submit">
                {busy ? "Creating account…" : "Create My Account"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
